import { PlayerGameData } from '../players/players.types'
import { TeamGameStats } from '../teams/teams.types'
import { API_ENDPOINTS } from '../utils/endpoints'
import { makeAPIRequest } from '../utils/request'
import { EventGQL, PlayLog } from './events.types'

/**
 * Retrieves all event information for a specified season.
 *
 * @param season - The year of the season to fetch events for.
 * @param includeCS - Whether to include championship series events.
 * @returns An array of event data for the season, or null if the request fails or returns an error.
 */
export async function fetchSeasonEvents(
  season: number,
  includeCS?: boolean,
  includeWLL?: boolean
): Promise<EventGQL[] | null> {
  try {
    const evsRes = await makeAPIRequest(API_ENDPOINTS.SEASON_EVENTS, {
      method: 'GET',
      apiVersion: '4',
      queryParams: {
        year: season,
        includeCS: includeCS || false,
        includeWLL: includeWLL || false,
      },
    })
    if (evsRes?.error) {
      evsRes.error?.message && console.error(evsRes.error?.message)
      return null
    }
    return evsRes?.data
  } catch (error) {
    return null
  }
}

/**
 * Retrieves detailed information for a single event identified by its slug.
 *
 * @param slug - The unique identifier for the event.
 * @returns The event data if found, or null if an error occurs or the event does not exist.
 */
export async function fetchSingleEventInfo(slug: string): Promise<EventGQL | null> {
  try {
    const evRes = await makeAPIRequest(`${API_ENDPOINTS.SEASON_EVENTS}/${slug}`, {
      method: 'GET',
      apiVersion: '4'
    })
    if (evRes?.error) {
      evRes.error?.message && console.error(evRes.error?.message)
      return null
    }
    return evRes?.data
  } catch (error) {
    console.error('Error fetching event info:', error)
    return null
  }
}

/**
 * REST endpoint - Fetch stats for each team
 * @returns Two teams team[0] is away team
 */
export async function fetchEventTeamsStats(slug: string): Promise<TeamGameStats[]> {
  try {
    const teamsStatsRes = await makeAPIRequest(
      `${API_ENDPOINTS.SEASON_EVENTS}/${slug}/teams/stats`,
      {
        method: 'GET',
        apiVersion: '4',
      }
    )
    if (teamsStatsRes?.error) {
      teamsStatsRes.error?.message && console.error(teamsStatsRes.error?.message)
      return []
    }
    return teamsStatsRes?.data ?? []
  } catch (error) {
    console.error('Error fetching event teams stats:', error)
    return []
  }
}

/**
 * REST endpoint - Fetch stats for each team
 * @returns Two teams team[0] is away team
 */
export async function fetchEventPlayerStats(slug: string): Promise<PlayerGameData[]> {
  try {
    const playerStatsRes = await makeAPIRequest(
      `${API_ENDPOINTS.SEASON_EVENTS}/${slug}/players/stats`,
      {
        method: 'GET',
        apiVersion: '4',
      }
    )
    if (playerStatsRes?.error) {
      playerStatsRes.error?.message && console.error(playerStatsRes.error?.message)
      return []
    }
    return playerStatsRes?.data ?? []
  } catch (error) {
    console.error('Error fetching player stats:', error)
    return []
  }
}

/**
 * REST endpoint - Fetch play logs for a single game
 */
export async function fetchEventPlayLogs(slug: string): Promise<PlayLog[]> {
  try {
    const playLogsRes = await makeAPIRequest(
      `${API_ENDPOINTS.SEASON_EVENTS}/${slug}/playlogs`,
      {
        method: 'GET',
        apiVersion: '4',
      }
    )
    if (playLogsRes?.error) {
      playLogsRes.error?.message && console.error(playLogsRes.error?.message)
      return []
    }
    return playLogsRes?.data
  } catch (error) {
    return []
  }
}
